import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Phone, User, Send, CheckCircle, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await axios.post(`${API}/contact`, formData); setSubmitted(true); toast.success("Message sent successfully!"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to send message"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center"><img src="/logo-v2.png" alt="SHIPP SMART" className="h-14 w-auto rounded-md" /></Link>
          <nav className="flex items-center gap-2">
            <Link to="/quote"><Button variant="ghost" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm">Get Quote</Button></Link>
            <ThemeToggle />
            <Link to="/customer"><Button className="bg-orange-600 hover:bg-orange-500 text-white text-sm rounded-lg">Login</Button></Link>
          </nav>
        </div>
      </header>

      <div className="relative h-44 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://images.pexels.com/photos/14020705/pexels-photo-14020705.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 to-slate-50 dark:to-slate-950" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <p className="text-orange-400 font-medium text-xs tracking-[0.25em] uppercase mb-2">Get In Touch</p>
            <h1 className="font-outfit font-bold text-3xl sm:text-4xl text-white tracking-tight">Contact Us</h1>
          </motion.div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-5 gap-8">
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="md:col-span-3">
            {submitted ? (
              <Card className="border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/5 rounded-xl">
                <CardContent className="p-12 text-center">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}><CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" /></motion.div>
                  <h3 className="font-outfit font-bold text-xl text-emerald-900 dark:text-emerald-300 mb-2">Message Sent!</h3>
                  <p className="text-emerald-700 dark:text-emerald-400 mb-6">We'll get back to you as soon as possible.</p>
                  <Button onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", message: "" }); }} variant="outline" className="border-emerald-300 dark:border-emerald-500/40 rounded-lg">Send Another Message</Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl shadow-sm">
                <CardContent className="p-8">
                  <h2 className="font-outfit font-semibold text-xl text-slate-900 dark:text-white mb-6">Send a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400">Name</Label>
                        <div className="relative"><User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input value={formData.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="John Doe" className="h-11 pl-10 rounded-lg" required data-testid="contact-name" /></div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400">Phone</Label>
                        <div className="relative"><Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input value={formData.phone} onChange={(e) => handleChange("phone", e.target.value)} placeholder="+1 234 567 890" className="h-11 pl-10 rounded-lg" required data-testid="contact-phone" /></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400">Email</Label>
                      <div className="relative"><Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input type="email" value={formData.email} onChange={(e) => handleChange("email", e.target.value)} placeholder="you@example.com" className="h-11 pl-10 rounded-lg" required data-testid="contact-email" /></div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400">Message</Label>
                      <Textarea value={formData.message} onChange={(e) => handleChange("message", e.target.value)} placeholder="How can we help you?" rows={5} required className="rounded-lg" data-testid="contact-message" />
                    </div>
                    <Button type="submit" disabled={loading} className="w-full h-11 bg-orange-600 hover:bg-orange-500 text-white font-medium rounded-lg transition-colors" data-testid="contact-submit-btn">
                      {loading ? "Sending..." : "Send Message"}<Send className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="md:col-span-2 space-y-4">
            {[
              { icon: MapPin, title: "USA Office", lines: ["8020 NW 66th Street BP-137803", "Miami, FL 33166-3900"], sub: "Tel: (786) 360-6446" },
              { icon: MapPin, title: "Haiti Office", lines: ["12 Imp Rodney Theocratie", "Vivy Mitchell, Petion Ville Haiti"], sub: "Tel: (509) 3720-9459" },
            ].map((office) => (
              <Card key={office.title} className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center shrink-0"><office.icon className="w-5 h-5 text-orange-600 dark:text-orange-400" /></div>
                    <div>
                      <h3 className="font-outfit font-semibold text-sm text-slate-900 dark:text-white mb-1">{office.title}</h3>
                      {office.lines.map((l, i) => <p key={i} className="text-slate-600 dark:text-slate-400 text-sm">{l}</p>)}
                      <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">{office.sub}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl shadow-sm">
              <CardContent className="p-6"><div className="flex items-start gap-4"><div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-orange-600 dark:text-orange-400" /></div><div><h3 className="font-outfit font-semibold text-sm text-slate-900 dark:text-white mb-1">Email</h3><p className="text-slate-600 dark:text-slate-400 text-sm">support@shippsmart.com</p><p className="text-slate-400 dark:text-slate-500 text-sm mt-1">We reply within 24 hours</p></div></div></CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl shadow-sm">
              <CardContent className="p-6"><div className="flex items-start gap-4"><div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" /></div><div><h3 className="font-outfit font-semibold text-sm text-slate-900 dark:text-white mb-1">Business Hours</h3><p className="text-slate-600 dark:text-slate-400 text-sm">Mon - Fri, 9AM - 6PM EST</p></div></div></CardContent>
            </Card>
            <Card className="bg-slate-900 dark:bg-slate-800 border-slate-800 dark:border-slate-700 rounded-xl">
              <CardContent className="p-6">
                <h3 className="font-outfit font-semibold text-sm text-white mb-3 uppercase tracking-wider">Quick Links</h3>
                <div className="space-y-2">
                  <Link to="/" className="block text-slate-400 hover:text-orange-400 text-sm transition-colors">Track a Shipment</Link>
                  <Link to="/quote" className="block text-slate-400 hover:text-orange-400 text-sm transition-colors">Get a Quote</Link>
                  <Link to="/customer" className="block text-slate-400 hover:text-orange-400 text-sm transition-colors">Customer Portal</Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-800 py-8 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 text-center"><p className="text-slate-400 text-sm">&copy; {new Date().getFullYear()} SHIPP SMART</p></div>
      </footer>
    </div>
  );
};

export default ContactPage;
